import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { handleBasicAlert } from "../../../core/data/redux/alerts";
import { useDispatch } from "react-redux";
import { authorizeUserAction } from "../../../core/data/redux/actions/AuthActions";
import MD5 from "crypto-js/md5";
import apiService from "../../../server";
import { CURRENT_USER } from "../../../core/data/redux/api";
import { formatSuccess } from "../../../core/data/redux/service/AuthService";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";

type PasswordField = "password";

const FEATURES = [
  {
    emoji: "📋",
    title: "Task Tracking",
    description: "Assign and track tasks in real time.",
  },
  {
    emoji: "👥",
    title: "Team Management",
    description: "Manage users, roles, and permissions.",
  },
  {
    emoji: "📊",
    title: "Kanban & Workflows",
    description: "Visual boards and approval workflows.",
  },
] as const;

const FLOATING_BADGES = [
  { emoji: "⚡", label: "Real-time updates", className: "login-page__float-badge--1" },
  { emoji: "🎯", label: "Smart workflows", className: "login-page__float-badge--2" },
  { emoji: "📱", label: "Mobile app", className: "login-page__float-badge--3" },
] as const;

// const STAT_CHIPS = ["⚡ Real-time updates", "🎯 Smart workflows", "🤝 Team-first"] as const;

const Login = () => {
  const dispatch = useDispatch<any>();
  const navigation = useNavigate();
  const [mobileNumber, setMobileNumber] = useState("");
  const fallBackUrlAndroid = "https://play.google.com/store/apps/details?id=in.hkcl.tasklync";
  const [otp, setOtp] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleLogin = async () => {
    if (!otp || !mobileNumber) {
      handleBasicAlert("Please enter valid credentials!", "OK", "error");
      return;
    }

    try {
      const encKey = await MD5(otp).toString();
      if (encKey) {
        dispatch(authorizeUserAction(mobileNumber, encKey, navigation));
      } else {
        handleBasicAlert("Encryption failed!", "OK", "error");
      }
    } catch (error) {
      console.error("Error during login process:", error);
      handleBasicAlert("An error occurred during login!", "OK", "error");
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const profile = sessionStorage.getItem('userProfile');
    if (!profile) return;

    apiService.get(CURRENT_USER).then(() => {
      const landing = JSON.parse(profile).landingPage;
      navigation(landing || '/index');
    }).catch(() => {
      sessionStorage.removeItem('userProfile');
    });
  }, [navigation]);

  const openMobileApp = (fallBackUrl: any) => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    let timeoutId: any;

    const clearFallback = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        clearFallback();
      }
    });

    if (isAndroid) {
      window.location.href = "intent://login#Intent;scheme=tasklync-hkcl;package=in.hkcl.tasklync.dev;end";
    } else if (isIOS) {
      formatSuccess("Coming Soon on Apple Store");
    } else {
      window.location.href = fallBackUrl;
    }

    timeoutId = setTimeout(() => {
      console.log("App not installed or deep link failed. Redirecting to store.");
      if (isAndroid) {
        window.location.href = fallBackUrl;
      }
    }, 2000);
  };

  return (
    <div className="login-page">
      <div className="login-page__decor login-page__decor--1" aria-hidden="true">📋</div>
      <div className="login-page__decor login-page__decor--2" aria-hidden="true">✨</div>
      <div className="login-page__decor login-page__decor--3" aria-hidden="true">🎯</div>

      {/* Left — home / landing */}
      <section className="login-page__hero">
        <div className="login-page__hero-content">
          <div className="login-page__hero-intro">
            <div className="login-page__badge-pill">
              <span aria-hidden="true">🌟</span> Task management made simple
            </div>

            <h1 className="login-page__headline">
              Empowering teams through <span>seamless</span> task management
            </h1>

            <p className="login-page__tagline">
              Plan, assign, and track work across your organisation — from daily
              tasks to complex workflows — all in one place.
            </p>

            {/* <div className="login-page__stat-chips">
              {STAT_CHIPS.map((chip) => (
                <span key={chip} className="login-page__stat-chip">{chip}</span>
              ))}
            </div> */}
          </div>

          <div className="login-page__hero-bottom">
            <div className="login-page__features">
              {FEATURES.map((feature) => (
                <div key={feature.title} className="login-page__feature">
                  <div className="login-page__feature-icon" aria-hidden="true">
                    {feature.emoji}
                  </div>
                  <div className="login-page__feature-text">
                    <h6>{feature.title}</h6>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="login-page__hero-visual" aria-hidden="true">
              <div className="login-page__hero-illustration">
                <ImageWithBasePath
                  src="assets/img/bg/login-hero-task.svg"
                  alt="Task management dashboard illustration"
                  className="login-page__hero-illustration-img"
                />
              </div>
              {FLOATING_BADGES.map((badge) => (
                <div key={badge.label} className={`login-page__float-badge ${badge.className}`}>
                  <span className="login-page__float-badge-emoji">{badge.emoji}</span>
                  <span className="login-page__float-badge-label">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="login-page__hero-footer mb-0">Copyright © 2025 - HKCL</p>
      </section>

      {/* Right — login form */}
      <aside className="login-page__panel">
        <div className="login-page__panel-inner">
          <div className="login-page__panel-logo">
            <img src="assets/tl-full.png" alt="TaskLync" />
          </div>

          <div className="login-page__panel-header text-center">
            
            <h2 className="mb-2">Welcome back! <span className="login-page__welcome-emoji" aria-hidden="true">👋</span></h2>
            <p className="mb-0">Sign in to continue to TaskLync</p>
          </div>

          <form className="login-page__form" onSubmit={(e) => e.preventDefault()}>
            <div className="mb-3">
              <label className="form-label">Email / Mobile Number</label>
              <div className="input-group">
                <input
                  type="text"
                  name="mobileNumber"
                  defaultValue=""
                  placeholder="Email / Mobile Number"
                  className="form-control border-end-0"
                  onChange={(e) => setMobileNumber(e.target.value)}
                />
                <span className="input-group-text border-start-0">
                  <i className="ti ti-mail" />
                </span>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Password</label>
              <div className="pass-group">
                <input
                  type={passwordVisibility.password ? "text" : "password"}
                  placeholder="Password"
                  name="otp"
                  className="pass-input form-control"
                  onChange={(e) => setOtp(e.target.value)}
                />
                <span
                  className={`ti toggle-passwords ${passwordVisibility.password ? "ti-eye" : "ti-eye-off"}`}
                  onClick={() => togglePasswordVisibility("password")}
                />
              </div>
            </div>

              
            <div className="mb-2">
              <button
                onClick={handleLogin}
                type="button"
                className="btn btn-primary w-100 login-page__signin-btn"
              >
                Sign In <span aria-hidden="true">→</span>
              </button>
            </div>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="text-end ms-auto">
                <Link to={all_routes.forgotPassword2} className="link-danger">
                  Forgot Password?
                </Link>
              </div>
            </div>
          </form>

          <div className="login-page__apps">
            {/* <p><span aria-hidden="true">📲</span> Download our mobile app</p> */}
            <div className="login-page__store-badges">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  openMobileApp(fallBackUrlAndroid);
                }}
                rel="noopener noreferrer"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  className="login-page__store-badge"
                />
              </a>

              <div className="login-page__store-disabled">
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="login-page__store-badge"
                />
                <span>Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Login;
